import React, { useState, useRef, useCallback } from 'react';
import { X, Loader2, Coins, ZoomIn, ZoomOut, Maximize2, Download } from 'lucide-react';
import type { MindmapResponse } from '../../../../lib/apis_vibe';
import { exportSvgCodeAsPng } from '../../../../lib/exportPng';

interface MindmapModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: MindmapResponse | null;
  loading: boolean;
}

const MIN_SCALE = 0.3;
const MAX_SCALE = 3;
const SCALE_STEP = 0.15;

const MindmapModal: React.FC<MindmapModalProps> = ({ isOpen, onClose, result, loading }) => {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const handleExport = async () => {
    if (!result?.svg_code) return;
    setIsExporting(true);
    try {
      const timestamp = result.mindmap_at
        ? new Date(result.mindmap_at).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      await exportSvgCodeAsPng(result.svg_code, `思维决策链-${timestamp}`);
    } catch (e) {
      console.error('Export failed', e);
      alert('导出失败，请稍后重试');
    } finally {
      setIsExporting(false);
    }
  };

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
    setScale(s => Math.max(MIN_SCALE, Math.min(MAX_SCALE, +(s + delta).toFixed(2))));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setTranslate(prev => ({ x: prev.x + dx, y: prev.y + dy }));
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale(s => Math.min(MAX_SCALE, +(s + SCALE_STEP).toFixed(2)));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(s => Math.max(MIN_SCALE, +(s - SCALE_STEP).toFixed(2)));
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  if (!isOpen) return null;

  const isCached = result?.status === 'cached';
  const totalTokens = result ? result.input_tokens + result.output_tokens : 0;
  const totalMoney = result ? (result.input_money || 0) + (result.output_money || 0) : 0;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-[1200px] max-w-[95vw] h-[85vh] bg-[#1a1a1a]/95 border border-cyan-500/30 rounded-2xl shadow-[0_0_50px_rgba(34,211,238,0.2)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 bg-gradient-to-r from-cyan-900/30 to-transparent border-b border-cyan-500/20 shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              🧠 思维决策链
            </h2>
            {result && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-400">
                  {result.message_count} 条消息
                </span>
                {isCached ? (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                    缓存命中
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300">
                    AI 实时生成
                  </span>
                )}
              </div>
            )}
          </div>

          {/* 缩放控制 */}
          {!loading && result && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                disabled={scale <= MIN_SCALE}
                className="p-1.5 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="缩小"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-400 w-12 text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={scale >= MAX_SCALE}
                className="p-1.5 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="放大"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={handleReset}
                className="p-1.5 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                title="重置"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <div className="w-px h-5 bg-white/10 mx-1" />
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="导出为 PNG"
              >
                {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                导出
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {loading && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* SVG 容器 */}
        <div
          className="flex-1 overflow-hidden relative bg-[#F7F9FC] cursor-grab active:cursor-grabbing"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* Loading 态 */}
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white/80 z-10">
              <Loader2 className="w-10 h-10 animate-spin text-cyan-500" />
              <p className="text-gray-600 text-sm">AI 生成思维链中，预计 40-90 秒...</p>
            </div>
          )}

          {/* SVG 渲染 */}
          {!loading && result && (
            <div
              className="absolute top-1/2 left-1/2 origin-center"
              style={{
                transform: `translate(-50%, -50%) translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                transformOrigin: 'center center',
                width: '1200px',
              }}
              dangerouslySetInnerHTML={{ __html: result.svg_code }}
            />
          )}

          {/* 空状态 */}
          {!loading && !result && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
              暂无思维链数据
            </div>
          )}
        </div>

        {/* 底部：Token 消耗 */}
        {!loading && result && (
          <div className="px-6 py-2 bg-black/40 border-t border-white/5 flex items-center justify-between text-xs text-gray-400 shrink-0">
            <div className="flex items-center gap-1">
              <Coins className="w-3.5 h-3.5" />
              {isCached ? (
                <span className="text-emerald-400">缓存命中（0 消耗）</span>
              ) : (
                <>
                  <span>Input: {result.input_tokens}</span>
                  <span className="mx-1">|</span>
                  <span>Output: {result.output_tokens}</span>
                  <span className="mx-1">|</span>
                  <span className="text-cyan-300">总计: {totalTokens} tokens</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!isCached && totalMoney > 0 && (
                <span className="text-yellow-400">
                  费用：¥{totalMoney.toFixed(4)}
                </span>
              )}
              {result.mindmap_at && (
                <span className="text-gray-500">
                  生成时间：{new Date(result.mindmap_at).toLocaleString('zh-CN')}
                </span>
              )}
              <span className="text-gray-500 italic">提示：滚轮缩放，拖拽平移</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MindmapModal;
